import { i18n } from '@/shared/i18n';

const MAX_PREVIEW_SECTIONS = 4;

interface Props {
  // The brief artifact payload crosses the registry/thumbnail seam untyped.
  data: unknown;
}

interface PreviewSection {
  name: string;
  position: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// The brief payload wraps the brief under `data.brief` (the widget schema is
// `{ brief: BriefGenerationDataType }`). Narrow defensively: an in-progress or
// malformed brief may arrive without a `brief` object, title, summary, or a
// well-formed sections array.
const readBrief = (data: unknown): Record<string, unknown> =>
  isRecord(data) && isRecord(data.brief) ? data.brief : {};

const readTitle = (brief: Record<string, unknown>): string =>
  typeof brief.title === 'string' && brief.title.length > 0
    ? brief.title
    : i18n.t('briefs:briefGeneration.briefGenerationThumbnail.brief');

const readSummary = (brief: Record<string, unknown>): string | null =>
  typeof brief.summary === 'string' && brief.summary.length > 0 ? brief.summary : null;

const readSections = (brief: Record<string, unknown>): PreviewSection[] => {
  const { sections } = brief;
  if (!Array.isArray(sections)) return [];
  return sections
    .filter(
      (section): section is Record<string, unknown> =>
        isRecord(section) && typeof section.name === 'string' && section.name.length > 0,
    )
    .map((section) => ({
      name: section.name as string,
      position: typeof section.position === 'number' ? section.position : 0,
    }))
    .sort((a, b) => a.position - b.position)
    .slice(0, MAX_PREVIEW_SECTIONS);
};

/**
 * Store-free, prop-only preview of a brief: the title, an optional summary, and
 * the first few section names ordered by position. Reads only `data.brief`
 * — never touches a widget store or the heavy TipTap section content. The brief
 * has no full-widget renderer, so this exists purely to live-render the
 * Library grid thumbnail.
 */
export const BriefGenerationThumbnail = (props: Props) => {
  const brief = readBrief(props.data);
  const title = readTitle(brief);
  const summary = readSummary(brief);
  const sections = readSections(brief);

  return (
    <div className="flex h-full flex-col gap-2 overflow-hidden">
      <p className="truncate text-sm font-semibold text-[#1A1A1A]">{title}</p>
      {summary && <p className="line-clamp-2 text-xs text-[#6C6962]">{summary}</p>}
      <ul className="flex flex-col gap-1">
        {sections.map((section) => (
          <li key={section.position} className="line-clamp-1 text-xs text-[#1A1A1A]">
            {section.name}
          </li>
        ))}
      </ul>
    </div>
  );
};
